import React from 'react'
import { theme } from 'antd'
import { fontSize, spacing } from '@vacationtracker/shared/constants/ThemeSetting'
import { ExclamationCircleFilled } from '@ant-design/icons'

/**
 * A secondary detail that is absent rather than present - "No holidays", "No employees".
 * Warning-coloured text with an icon, not a badge: badges are for the title row,
 * and a row of them would shout louder than the card's actual name.
 *
 * The filled circle is distinct from the triangle on the flag badges: a missing
 * fact is a nudge, an escalated flag is a warning.
 */
/** `size` follows the facts line it sits in, so a missing fact is never smaller than the ones beside it. */
export function RecordCardMissing({ children, label, size = 'sm' }: { children?: React.ReactNode; label?: string; size?: 'sm' | 'base' }) {
  const { token } = theme.useToken()

  if (!children) {
    return <ExclamationCircleFilled role="img" aria-label={label} title={label} style={{ fontSize: fontSize.sm, color: token.colorWarning }} />
  }

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: spacing.xxs,
        minWidth: 0,
        color: token.colorWarningText,
        fontSize: fontSize[size],
      }}
    >
      <ExclamationCircleFilled aria-hidden style={{ fontSize: fontSize[size], color: token.colorWarning }} />
      {children}
    </span>
  )
}

/** Middot-separated detail line. Falsy children are dropped so callers can inline conditionals. */
export function RecordCardFacts({ children }: { children: React.ReactNode }) {
  const { token } = theme.useToken()
  const facts = React.Children.toArray(children).filter(Boolean)

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: spacing.xs,
        minWidth: 0,
        flexWrap: 'wrap',
        overflow: 'hidden',
      }}
    >
      {facts.map((fact, index) => (
        <React.Fragment key={index}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: spacing.xxs, whiteSpace: 'nowrap' }}>
            {index > 0 && (
              <span aria-hidden style={{ color: token.colorTextQuaternary }}>
                ·
              </span>
            )}
            {fact}
          </span>
        </React.Fragment>
      ))}
    </div>
  )
}
